import { DEFAULT_POLICY, DEFAULT_POLICY_VALUE } from 'app/utils/constants';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from 'app/services/apiService';
import { setChanges, setWithoutTemplate } from './privacyPersistedSlice';

const initialState = {
  allPolicies: DEFAULT_POLICY_VALUE,
  statuses: [],
  policyTemplateTypes: [],
  isPolicyTemplateTypesFetched: false,
  policyTypes: [],
  isPolicyTypesFetched: false,
  loading: false,
  users: [],
  areUsersFetched: false,
  privacyError: null,
};

export const getAllPolicies = createAsyncThunk('allPolicies', async (_, { rejectWithValue }) => {
  try {
    const { data } = await apiService.fetchAllPolicies();
    return data?.policies;
  } catch (error) {
    rejectWithValue(error.message);
    return error;
  }
});

export const getPolicyTemplateTypes = createAsyncThunk('policyTemplateTypes', async payload => {
  try {
    const { data } = await apiService.fetchPolicyTypes(payload);
    return data;
  } catch (error) {
    console.error(error);
    return error;
  }
});

export const getAllPoliciesTypes = createAsyncThunk('policiesTypes', async () => {
  try {
    const { data } = await apiService.fetchAllPolicyTypes();
    return data?.policyTypes;
  } catch (error) {
    console.error(error);
    return error;
  }
});

export const deletePolicyByID = createAsyncThunk(
  'deletePolicyCompletely',
  async (data, { dispatch, rejectWithValue }) => {
    try {
      await apiService.deletePolicyCompletelyByID(data.id);
      dispatch(setWithoutTemplate({}));
    } catch (error) {
      dispatch(getAllPolicies());
      return rejectWithValue(error);
    }
  }
);

export const deletePolicyByCache = createAsyncThunk(
  'deletePolicyByCache',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await apiService.deletePolicyCacheByID(payload);
      dispatch(setChanges(false));
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const savePolicy = createAsyncThunk(
  'savePolicy',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await apiService.savePolicy(payload);
      dispatch(deletePolicyByCache(payload));
      dispatch(setChanges(false));
      dispatch(getAllPolicies());
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const repositionColumns = createAsyncThunk('reposition', async (payload, { dispatch }) => {
  try {
    const response = await apiService.repositionColumns(payload);
    dispatch(setChanges(true));
    return response;
  } catch (error) {
    console.error(error);
  }
});

export const getUsers = createAsyncThunk('users', async () => {
  try {
    const response = await apiService.getUsers();
    return response.data.data;
  } catch (error) {
    console.error(error);
  }
});

export const deleteDataClassLevel = createAsyncThunk(
  'deleteColumn',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiService.deleteDataClassLevel(payload);
      dispatch(setChanges(true));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete data class level');
    }
  }
);

export const updateNameOrDescDataColumns = createAsyncThunk(
  'updateColumns',
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiService.updateNameOrDescDataColumns(payload);
      dispatch(setChanges(true));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update columns');
    }
  }
);

export const activateOrDeactivatePolicy = createAsyncThunk(
  'activateOrDeactivatePolicy',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await apiService.activatePolicy(payload.policyType, {
        active: payload.action,
      });
      dispatch(getAllPolicies());
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const privacySlice = createSlice({
  name: 'privacy',
  initialState,
  reducers: {
    setLoading(state, { payload }) {
      state.loading = payload;
    },
    setPolicyStateEmpty(state) {
      state.allPolicies = null;
    },
    setPrivacyError(state, { payload }) {
      state.privacyError = payload;
    },
  },

  extraReducers(builder) {
    builder.addCase(getPolicyTemplateTypes.fulfilled, (state, { payload }) => {
      state.policyTemplateTypes = {
        ...payload,
        templates: [
          ...payload.templates,
          {
            id: 'scratch',
            name: 'Empty Template',
            policyType: DEFAULT_POLICY,
            description: 'This is a empty Template',
          },
        ],
      };
      state.isPolicyTemplateTypesFetched = true;
    });
    builder.addCase(getAllPolicies.pending, state => {
      state.loading = true;
    });
    builder.addCase(getAllPolicies.fulfilled, (state, { payload }) => {
      if (payload) {
        const firstPolicy = payload['uuid-for-icah-policy-type'];
        state.statuses = firstPolicy.statuses;

        if (firstPolicy) {
          const status = firstPolicy.statuses['Active'];
          const saved = firstPolicy.statuses['Saved'];

          if (firstPolicy.beingEdited) {
            const beingEditedStatus = firstPolicy.statuses['Draft'];
            if (beingEditedStatus) {
              state.allPolicies = {
                id: beingEditedStatus.id || '',
                policyType: firstPolicy.policyType || '',
                templateId: beingEditedStatus.templateId || '',
                name: beingEditedStatus.name || '',
                comment: beingEditedStatus.comment || '',
                created: beingEditedStatus.created || '',
                lastSaved: beingEditedStatus.lastSaved || '',
                activationDates: firstPolicy.activationDates || [],
                isActive: false,
                beingEdited: true,
                lockedBy: firstPolicy.lockedBy,
              };
              state.loading = false;

              return;
            }
          } else if (status) {
            state.allPolicies = {
              id: status.id || '',
              policyType: firstPolicy.policyType || '',
              templateId: status.templateId || '',
              name: status.name || '',
              comment: status.comment || '',
              created: status.created || '',
              lastSaved: status.lastSaved || '',
              activationDates: firstPolicy.activationDates || [],
              isActive: true,
              beingEdited: false,
              lockedBy: firstPolicy.lockedBy,
            };
            state.loading = false;

            return;
          } else if (!firstPolicy.isActive && !firstPolicy.beingEdited) {
            state.allPolicies = {
              id: saved?.id || '',
              policyType: firstPolicy.policyType || '',
              templateId: saved?.templateId || '',
              name: saved?.name || '',
              comment: saved?.comment || '',
              created: saved?.created || '',
              lastSaved: saved?.lastSaved || '',
              activationDates: firstPolicy.activationDates || [],
              isActive: false,
              beingEdited: false,
              lockedBy: firstPolicy.lockedBy,
            };
            state.loading = false;

            return;
          }
        }
      }
      state.allPolicies = DEFAULT_POLICY_VALUE;
      state.loading = false;
    });

    builder.addCase(getAllPolicies.rejected, (state, { payload }) => {
      state.privacyError = payload.response.data;
      state.loading = false;
    });
    builder.addCase(getAllPoliciesTypes.fulfilled, (state, { payload }) => {
      state.policyTypes = payload;
      state.isPolicyTypesFetched = true;
    });
    builder.addCase(deletePolicyByID.pending, state => {
      state.loading = true;
    });

    builder.addCase(deletePolicyByID.fulfilled, state => {
      state.allPolicies = initialState.allPolicies;
      state.loading = false;
    });
    builder.addCase(deletePolicyByID.rejected, (state, { payload: { response } }) => {
      state.privacyError = response.data;
      state.loading = false;
    });
    builder.addCase(getUsers.fulfilled, (state, { payload }) => {
      state.users = payload;
      state.areUsersFetched = true;
    });

    builder.addCase(activateOrDeactivatePolicy.pending, state => {
      state.loading = true;
    });
    // fulfilled cases not required because need to fetch policies again.
    builder.addCase(activateOrDeactivatePolicy.rejected, (state, { payload }) => {
      state.privacyError = payload.response.data;
      state.loading = false;
    });

    builder.addCase(savePolicy.pending, state => {
      state.loading = true;
    });
    // fulfilled cases not required because need to fetch policies again.
    builder.addCase(savePolicy.rejected, (state, { payload }) => {
      state.privacyError = payload.response.data;
      state.loading = false;
    });

    builder.addCase(deletePolicyByCache.pending, state => {
      state.loading = true;
    });

    builder.addCase(deletePolicyByCache.fulfilled, state => {
      state.loading = false;
    });

    builder.addCase(deletePolicyByCache.rejected, (state, { payload }) => {
      state.privacyError = payload.response.data;
      state.loading = false;
    });
  },
});

export const { setLoading, setPolicyStateEmpty, setPrivacyError } = privacySlice.actions;
export default privacySlice.reducer;
